import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, MessageCircle, CheckCircle2, ChevronRight, Package } from "lucide-react";
import { Reveal } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { API, money, stockLabel, WHATSAPP_URL, CATEGORY_LABELS, setJsonLd, removeJsonLd, SITE_ORIGIN } from "@/lib/site";

export default function ProductDetail({ forcedSlug } = {}) {
  const { slug: paramSlug } = useParams();
  const slug = forcedSlug || paramSlug;
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [master, setMaster] = useState({ company: "", details: "" });
  const { addItem, setOpen } = useCart();

  const handleAdd = () => {
    if (p.requires_master_details) {
      if (!master.company.trim()) { toast.error("Master company/name is required for this Slave package"); return; }
      addItem(p, 1, {
        master_company: master.company.trim(),
        master_details: master.details.trim(),
        currency: p.currency,
        summary: `Master: ${master.company.trim()}${master.details.trim() ? `\nFLEX details: ${master.details.trim()}` : ""}`,
      });
    } else {
      addItem(p);
    }
    setOpen(true);
  };

  useEffect(() => {
    setP(null); setNotFound(false); setImg(0);
    axios.get(`${API}/products/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!p) return;
    document.title = p.seo_title || `${p.name} | Regal Tuning`;
    if (p.seo_description) {
      document.querySelector('meta[name="description"]')?.setAttribute("content", p.seo_description);
    }
  }, [p]);

  useEffect(() => {
    if (!p) return;
    const origin = SITE_ORIGIN;
    setJsonLd("ld-product", {
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.name,
      image: (p.images || []).map((i) => (i.startsWith("http") ? i : origin + i)),
      description: p.seo_description || (p.description || "").slice(0, 300),
      brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
      sku: p.sku,
      offers: {
        "@type": "Offer",
        url: origin + (forcedSlug ? `/${forcedSlug}` : `/product/${p.slug}`),
        priceCurrency: p.currency || "GBP",
        price: p.sale_price || p.price_ex_vat,
        availability: p.stock === "out" ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      },
    });
    return () => removeJsonLd("ld-product");
  }, [p, forcedSlug]);

  if (notFound) {
    return (
      <main className="grid min-h-[70vh] place-items-center pt-24" data-testid="product-not-found">
        <div className="text-center">
          <p className="font-display text-2xl font-bold uppercase">Product not found</p>
          <Link to="/tuning-tools" className="mt-5 inline-block border border-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber">Back to shop</Link>
        </div>
      </main>
    );
  }

  if (!p) {
    return <main className="container-regal pt-40 pb-24"><div className="aspect-video animate-pulse border border-line bg-card" /></main>;
  }

  const out = p.stock === "out";

  return (
    <main data-testid="product-detail-page" className="pt-28 sm:pt-36 pb-24">
      <div className="container-regal">
        <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="breadcrumbs">
          <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
          <ChevronRight size={12} />
          <Link to={`/tuning-tools/${p.category}`} className="hover:text-regal-amber">{CATEGORY_LABELS[p.category]}</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{p.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="relative overflow-hidden border border-line bg-card">
              <img src={p.gallery[img]} alt={p.name} className="aspect-[4/3] w-full object-cover" data-testid="product-main-image" />
              {p.badge && <span className={`absolute left-4 top-4 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${p.badge === "MASTER" ? "bg-regal-amber text-void" : "bg-elevated border border-linebright text-neutral-200"}`}>{p.badge}</span>}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {p.gallery.map((g, i) => (
                <button key={i} onClick={() => setImg(i)} data-testid={`gallery-thumb-${i}`} className={`overflow-hidden border transition-colors ${i === img ? "border-regal-amber" : "border-line hover:border-linebright"}`}>
                  <img src={g} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neutral-500">{p.brand}</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.05]" data-testid="product-title">{p.name}</h1>
            <div className="mt-4 flex items-center gap-4">
              <span className={`flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] ${out ? "text-regal-red" : "text-emerald-600"}`} data-testid="product-stock-status">
                <span className={`h-2 w-2 rounded-full ${out ? "bg-regal-red" : "bg-emerald-500"}`} /> {stockLabel(p.stock)}
              </span>
            </div>
            <div className="mt-6 border-y border-line py-6">
              <p className="font-display text-4xl font-extrabold text-regal-amber" data-testid="product-price">{p.price_ex_vat > 0 ? money(p.effective_price ?? p.price_ex_vat, p.currency) : "POA"}</p>
              {p.sale_price > 0 && <p className="mt-1 font-mono text-sm text-neutral-500 line-through">{money(p.price_ex_vat, p.currency)}</p>}
            </div>
            <p className="mt-6 text-base leading-relaxed text-neutral-300" data-testid="product-description">{p.description}</p>
            {p.requires_master_details && (
              <div className="mt-6 border border-regal-amber/30 bg-regal-amber/5 p-5" data-testid="master-details-fields">
                <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">Master tool details — required</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input placeholder="Master company/name *" value={master.company} onChange={(e) => setMaster({ ...master, company: e.target.value })} data-testid="master-company-input" className="border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors" />
                  <input placeholder="Master FLEX details / serial (if applicable)" value={master.details} onChange={(e) => setMaster({ ...master, details: e.target.value })} data-testid="master-details-input" className="border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors" />
                </div>
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={handleAdd}
                disabled={out || p.price_ex_vat === 0}
                data-testid="product-add-to-cart-button"
                className="inline-flex items-center gap-2.5 bg-regal-amber px-9 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-void hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ShoppingCart size={17} /> Add to Cart
              </button>
              {WHATSAPP_URL && (
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp-button"
                  className="inline-flex items-center gap-2.5 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-neutral-100 hover:border-regal-amber hover:text-regal-amber transition-colors"
                >
                  <MessageCircle size={17} /> Enquire
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="border border-line bg-card p-7 sm:p-9" data-testid="product-specs">
              <p className="overline-label mb-6">Specifications</p>
              <dl className="divide-y divide-line">
                {Object.entries(p.specs).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-6 py-3.5">
                    <dt className="font-mono text-xs uppercase tracking-[0.14em] text-neutral-500">{k === "Warranty" ? "Manufacturer warranty" : k}</dt>
                    <dd className="text-right text-sm text-neutral-200">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-line bg-card p-7 sm:p-9" data-testid="product-includes">
              <p className="overline-label mb-6">What's included</p>
              <ul className="space-y-3.5">
                {p.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-sm text-neutral-200">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-regal-amber" /> {inc}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex items-center gap-3 border border-line bg-surface p-4">
                <Package size={18} className="shrink-0 text-regal-amber" />
                {p.physical === false ? (
                  <p className="text-xs leading-relaxed text-neutral-400" data-testid="product-delivery-digital">Electronic product — activated or delivered by email on your tool serial number, usually the same working day. No physical shipping.</p>
                ) : (
                  <p className="text-xs leading-relaxed text-neutral-400" data-testid="product-delivery-physical">Worldwide tracked & insured delivery. Dispatch within 1–2 working days on stocked items.</p>
                )}
              </div>
            </div>
          </Reveal>
        </div>

        {p.related?.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <Reveal><p className="overline-label mb-8">Related products</p></Reveal>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {p.related.map((r) => <ProductCard key={r.slug} p={r} />)}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
